import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface BrandingConfigProps {
  config: {
    companyName: string
    companyTagline: string
    primaryEmail: string
    phone: string
    primaryColor: string
    secondaryColor: string
    accentColor: string
  }
  onConfigChange: (config: any) => void
  onSave?: () => void
  onReset?: () => void
  saving?: boolean
}

export function BrandingConfig({ config, onConfigChange, onSave, onReset, saving = false }: BrandingConfigProps) {
  const handleInputChange = (field: string, value: string) => {
    onConfigChange({
      ...config,
      [field]: value
    })
  }

  const colorPresets = [
    { name: "Corporate Blue", primary: "#1e40af", secondary: "#3b82f6", accent: "#60a5fa" },
    { name: "Professional Green", primary: "#047857", secondary: "#059669", accent: "#34d399" },
    { name: "Modern Purple", primary: "#7c3aed", secondary: "#8b5cf6", accent: "#a78bfa" },
    { name: "Classic Red", primary: "#dc2626", secondary: "#ef4444", accent: "#f87171" },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand Configuration</CardTitle>
        <CardDescription>
          Customize your organization's branding for security reports
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input
              id="company-name"
              value={config.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company-tagline">Company Tagline <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input
              id="company-tagline"
              value={config.companyTagline}
              onChange={(e) => handleInputChange('companyTagline', e.target.value)}
              placeholder="Your company tagline or slogan"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="primary-email">Primary Email <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                id="primary-email"
                type="email"
                value={config.primaryEmail}
                onChange={(e) => handleInputChange('primaryEmail', e.target.value)}
                placeholder="contact@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                id="phone"
                type="tel"
                value={config.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

        </div>

        <div className="space-y-4">
          <Label className="text-base font-semibold">Color Scheme</Label>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="primary-color">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={config.primaryColor}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={config.primaryColor}
                  onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                  placeholder="#1e40af"
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondary-color">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondary-color"
                  type="color"
                  value={config.secondaryColor}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={config.secondaryColor}
                  onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="accent-color">Accent Color</Label>
              <div className="flex gap-2">
                <Input
                  id="accent-color"
                  type="color"
                  value={config.accentColor}
                  onChange={(e) => handleInputChange('accentColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={config.accentColor}
                  onChange={(e) => handleInputChange('accentColor', e.target.value)}
                  placeholder="#60a5fa"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Quick Presets</Label>
            <div className="grid gap-2 md:grid-cols-2">
              {colorPresets.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  className="justify-start h-auto p-3"
                  onClick={() => onConfigChange({
                    ...config,
                    primaryColor: preset.primary,
                    secondaryColor: preset.secondary,
                    accentColor: preset.accent
                  })}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: preset.primary }}
                      />
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: preset.secondary }}
                      />
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: preset.accent }}
                      />
                    </div>
                    <span className="text-sm">{preset.name}</span>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onReset}
            disabled={saving}
          >
            Reset to Default
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}